import { ReactElement, useState } from "react";

import Grid from "@mui/material/Grid";
import { FilterType, FilterOperationEnum, FieldName, getDateMinusNMonths, OrganizationCategory } from "@simplyk/common";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { AmplitudeEvents } from "../../constants/amplitude";
import { useCurrentUserContext } from "../../contexts/CurrentUserContext";
import { trpc } from "../../helpers/trpc";
import { isRequired } from "../../helpers/validators";
import { useAmplitude } from "../../hooks/amplitude/useAmplitude";
import { useTranslate } from "../../hooks/useTranslate";

import { DashboardDialog } from "@/dashboard-design-system/Dialog";
import { DashboardRadioGroup } from "@/dashboard-design-system/RadioGroup";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

export const OrganizationCategoryPopup = () => {
  const { organization, refetchOrganization } = useCurrentUserContext();
  const hasOrganizationAlreadyAnswered = organization?.category;

  if (!organization || hasOrganizationAlreadyAnswered) {
    return null;
  }

  return <Popup refetchOrganization={refetchOrganization} />;
};

const date = getDateMinusNMonths(new Date(), 1).toISOString();

interface PopupProps {
  refetchOrganization: () => void;
}

const Popup = ({ refetchOrganization }: PopupProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { logAmplitudeEvent } = useAmplitude();
  const { t } = useTranslate();
  const { mutateAsync: updateOrganizationCategory } = trpc.updateOrganizationCategory.useMutation();
  const [isOpen, setIsOpen] = useState(true);

  const { data: paymentCount } = trpc.getTransactionCount.useQuery({
    limit: 10,
    cursor: 0,
    filters: [
      [
        {
          fieldName: FieldName.TransactionTableCreatedAtUtc,
          operation: FilterOperationEnum.Before,
          type: FilterType.Date,
          values: [date],
        },
      ],
    ],
  });

  const hasFourPaymentsOlderThanThirtyDays = paymentCount && paymentCount >= 4;

  const form = useForm<{ category: OrganizationCategory }>();

  if (!hasFourPaymentsOlderThanThirtyDays) {
    return null;
  }

  const categoryArray = Object.keys(OrganizationCategory) as Array<keyof typeof OrganizationCategory>;
  const values = categoryArray.reduce(
    (prev, current) => {
      if (current === OrganizationCategory.Other) {
        return prev;
      }
      return {
        ...prev,
        [current]: t("dashboard", `${current}` as never),
      };
    },
    {} as Record<OrganizationCategory, ReactElement<unknown>>
  );

  const handleSubmit = form.handleSubmit(async (data: { category: OrganizationCategory }) => {
    logAmplitudeEvent(AmplitudeEvents.SubmitOrgCategoryPopup);

    const { error } = await updateOrganizationCategory({ category: data.category });

    if (error) {
      enqueueSnackbar(t("dashboard", "anErrorOccurred"), { vibe: "danger" });
      return;
    }

    await refetchOrganization();
    enqueueSnackbar(t("dashboard", "organizationCategoryPopup.success"), { vibe: "positive" });
    setIsOpen(false);
  });

  return (
    <DashboardDialog
      title=""
      maxWidth="sm"
      fullWidth
      actions={{
        main: { onClick: handleSubmit, children: t("common", "Submit") },
      }}
      open={isOpen}
      hideIcon
    >
      <Grid container sx={(theme) => ({ padding: theme.spacing(4) })}>
        <Grid item xs={12} sx={(theme) => ({ marginBottom: theme.spacing(0.5) })}>
          <DashboardTypography variant="subtitle2" vibe="secondary-main">
            {t("dashboard", "organizationCategoryPopup.title")}
          </DashboardTypography>
        </Grid>
        <Grid item xs={12} sx={(theme) => ({ marginBottom: theme.spacing(1.5) })}>
          <DashboardTypography variant="h6">{t("dashboard", "organizationCategoryPopup.subtitle")}</DashboardTypography>
        </Grid>
        <Grid item xs={12}>
          <Grid container>
            <DashboardRadioGroup
              values={{ ...values, [OrganizationCategory.Other]: t("dashboard", "Other") }}
              name="category"
              control={form.control}
              columns={2}
              fullWidth
              errorMessage={form.formState.errors["category"]?.message}
              rules={[isRequired(t("common", "fieldIsRequired"))]}
            />
          </Grid>
        </Grid>
      </Grid>
    </DashboardDialog>
  );
};
